import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { Toast } from '@components';
import ViewControls from "./components/ViewControls";
import OperationCardList from "./components/OperationCardList";
import { Operation, fetchAgencyOperations } from "../../../networkRequest/operationsService";
import { useUser } from "../../../hooks/useUser";
import { useAgency } from "../../../hooks/useAgency";
import { PageTitle } from "../components";
import { Add as AddIcon } from '@mui/icons-material';
import { PAGE_PATHS } from '../../../constants';

const ListOperationsPage: React.FC = () => {
  const { user } = useUser();
  const { agency } = useAgency();
  const navigate = useNavigate();

  const [operations, setOperations] = useState<Operation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [sortBy, setSortBy] = useState<string>('Date');

  useEffect(() => {
    const fetchOperations = async () => {
      if (!user?.accessToken || !agency?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data, error } = await fetchAgencyOperations(user.accessToken, agency.id);

        if (error) {
          setError(error);
        } else if (data) {
          setOperations(data);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch operations');
      } finally {
        setLoading(false);
      }
    };

    fetchOperations();
  }, [user?.accessToken, agency?.id]);

  const filteredOperations = operations.filter(op => {
    if (statusFilter === 'All') return true;
    return op.status.toLowerCase() === statusFilter.toLowerCase();
  });

  const sortedOperations = [...filteredOperations].sort((a, b) => {
    if (sortBy === 'Date') {
      return new Date(b.start_date).getTime() - new Date(a.start_date).getTime();
    } else if (sortBy === 'Title') {
      return a.title.localeCompare(b.title);
    }
    return 0;
  });

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between"
        }}>
        <PageTitle title="Operations" />
        <Button 
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => navigate(`${PAGE_PATHS.operations}/create`)}
        >
          Operation
        </Button>
      </Stack>
      <ViewControls
        statusFilter={statusFilter}
        sortBy={sortBy}
        onStatusFilterChange={setStatusFilter}
        onSortByChange={setSortBy}
        resultsCount={sortedOperations.length}
      />
      <OperationCardList
        operations={sortedOperations}
        loading={loading}
        statusFilter={statusFilter}
        onOperationsChange={setOperations}
        onError={setError}
        allOperations={operations}
      />
      {/* Error Toast */}
      {error && (
        <Toast type="error" onClose={() => setError('')}>
          {error}
        </Toast>
      )}
    </Box>
  );
};

export default ListOperationsPage;
